import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { TNaerasPanel } from '@/data/types';

interface PanelCardProps<T> {
  title: string;
  subtitle?: React.ReactNode;
  /** Undefined while the first response is still on its way. */
  panel: TNaerasPanel<T> | undefined;
  /** Shown beside the title, only once the panel has data. */
  aside?: (data: T) => React.ReactNode;
  children: (data: T) => React.ReactNode;
  delay?: number;
  className?: string;
}

/**
 * The frame every dashboard chart sits in.
 *
 * One panel failing is not an error for the page: the API answers 200 and that
 * panel alone comes back `available: false` with a Greek `reason`. The card
 * then shows the reason where the chart would be — never a chart of zeros.
 */
export function PanelCard<T>({
  title,
  subtitle,
  panel,
  aside,
  children,
  delay = 0,
  className = '',
}: PanelCardProps<T>) {
  const data = panel?.available ? (panel as { available: true } & T) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={`h-full ${className}`}
    >
      <div className="flex h-full flex-col rounded-3xl border border-gray-100/80 bg-white/90 p-6 shadow-xl shadow-gray-900/5 backdrop-blur-sm transition-shadow duration-300 hover:shadow-2xl sm:p-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">{title}</h3>
            {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
          </div>
          {data && aside?.(data)}
        </div>

        {!panel ? (
          <div className="h-56 animate-pulse rounded-2xl bg-gray-100" />
        ) : !data ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 px-6 py-12 text-center">
            <Clock className="h-6 w-6 text-gray-400" />
            <p className="max-w-md text-sm font-medium text-gray-600">
              {(panel as { reason: string }).reason}
            </p>
          </div>
        ) : (
          children(data)
        )}
      </div>
    </motion.div>
  );
}

/** One figure in a card's KPI row. */
export const Kpi: React.FC<{
  label: string;
  value: string;
  sub?: React.ReactNode;
  tone?: 'hybrid' | 'hydro' | 'neutral';
}> = ({ label, value, sub, tone = 'neutral' }) => {
  const styles = {
    hybrid: 'border-emerald-100 bg-emerald-50/70 text-emerald-700',
    hydro: 'border-blue-100 bg-blue-50/70 text-blue-700',
    neutral: 'border-slate-100 bg-slate-50 text-slate-600',
  }[tone];

  return (
    <div className={`rounded-2xl border p-3 ${styles}`}>
      <span className="block text-[10px] font-bold uppercase tracking-wider">{label}</span>
      <span className="block text-lg font-black text-gray-900">{value}</span>
      {sub && <span className="block text-[11px] font-medium text-gray-500">{sub}</span>}
    </div>
  );
};

export default PanelCard;
